/**
 * Registers the service worker and — just as important — makes sure a new
 * deploy actually takes over. This app has been bitten by a bundle that never
 * updated, so: the worker script is never read from the HTTP cache, updates
 * are checked on every load, on focus and every half hour, and the page tells
 * the user rather than swapping the app out from under a half-filled form.
 */

import { setOfflineState } from './store';

const SW_URL = `${process.env.PUBLIC_URL || ''}/service-worker.js`;
const UPDATE_INTERVAL_MS = 30 * 60 * 1000;

let registration: ServiceWorkerRegistration | null = null;
let reloading = false;
let updateAccepted = false;

const supported = (): boolean => typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

export function registerServiceWorker(): void {
  if (!supported()) return;

  if (process.env.NODE_ENV !== 'production') {
    // `react-scripts start` never builds a worker; an old one left behind
    // would serve a stale bundle for the rest of the day.
    void unregisterServiceWorker();
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(SW_URL, { updateViaCache: 'none' })
      .then((reg) => {
        registration = reg;
        watchForUpdate(reg);
        if (reg.waiting && navigator.serviceWorker.controller) {
          setOfflineState({ updateReady: true });
        }
        setInterval(() => void reg.update().catch(() => undefined), UPDATE_INTERVAL_MS);
      })
      .catch(() => {
        /* no worker: the app still works, just without offline reading */
      });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void checkForUpdate();
    });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // Only reload when the user asked for the update; the first install
      // claims the page too, and reloading then would loop.
      if (!updateAccepted || reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}

function watchForUpdate(reg: ServiceWorkerRegistration): void {
  reg.addEventListener('updatefound', () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      if (installing.state !== 'installed') return;
      if (navigator.serviceWorker.controller) {
        // a newer build is ready and waiting for a free moment
        setOfflineState({ updateReady: true });
      }
    });
  });
}

export async function checkForUpdate(): Promise<void> {
  try {
    await registration?.update();
  } catch {
    /* offline: nothing to check */
  }
}

/** Swap in the waiting build and reload. Called from the "Update" pill. */
export async function applyUpdate(): Promise<void> {
  if (!supported()) return;
  const reg = registration ?? (await navigator.serviceWorker.getRegistration()) ?? null;
  const waiting = reg?.waiting;
  updateAccepted = true;
  if (!waiting) {
    window.location.reload();
    return;
  }
  waiting.postMessage({ type: 'SKIP_WAITING' });
  // If the swap does not happen quickly, reload anyway rather than sit stale.
  setTimeout(() => {
    if (!reloading) {
      reloading = true;
      window.location.reload();
    }
  }, 3000);
}

/** Which build is serving this page — used to prove an update took over. */
export function activeVersion(): Promise<string | null> {
  const controller = supported() ? navigator.serviceWorker.controller : null;
  if (!controller) return Promise.resolve(null);
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), 1500);
    channel.port1.onmessage = (event) => {
      clearTimeout(timer);
      resolve(event.data?.version ?? null);
    };
    controller.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
  });
}

export async function unregisterServiceWorker(): Promise<void> {
  if (!supported()) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((reg) => reg.unregister()));
    if (typeof caches !== 'undefined') {
      const names = await caches.keys();
      await Promise.all(
        names.filter((name) => name.startsWith('staketory-')).map((name) => caches.delete(name)),
      );
    }
  } catch {
    /* nothing to undo */
  }
}
